// progressStore.ts
//
// Lightweight external store for live agent-run progress. nova-os emits
// rich progress as AG-UI CUSTOM events (nova.phase / nova.plan / nova.step /
// nova.skill_*), gated server-side by NOVA_OS_STREAM_PHASES / _PLAN. The
// @assistant-ui/react aggregator drops CUSTOM events from ThreadState
// (they fall through its default branch), so LiveAgentActivity can't read
// them from the runtime. We tap them at the transport layer
// (HttpAgentWithContext.runAgent → onCustomEvent) and feed this store;
// LiveAgentActivity subscribes via useSyncExternalStore.
//
// Event value shapes vary by nova-os build, so every reader is defensive.

export type StepStatus = "pending" | "started" | "done" | "error";

export interface PlanStep {
  id: string;
  description: string;
  capability: string;
  status: StepStatus;
  elapsedMs?: number;
  /** epoch ms when this step transitioned to "started" — drives the live
   *  running-timer in the UI (final time comes from elapsedMs). */
  startedAt?: number;
}

export interface SkillRow {
  /** capability (stable per skill dispatch) — used to correlate start/finish. */
  key: string;
  skill: string;
  capability: string;
  status: "running" | "done" | "failed";
  elapsedMs?: number;
  error?: string;
}

export interface ProgressState {
  running: boolean;
  phase: string | null;
  /** Upfront planned steps (nova.plan), status updated by nova.step. */
  steps: PlanStep[];
  /** Live skill dispatches (nova.skill_*) — always available, the most
   *  reliable signal; rendered when no plan steps were emitted. */
  skills: SkillRow[];
  /** epoch ms when the run started — drives the inline running timer. */
  runStartedAt: number | null;
}

const EMPTY: ProgressState = {
  running: false,
  phase: null,
  steps: [],
  skills: [],
  runStartedAt: null,
};

// Per-conversation storage (#24). Each conversation keeps its own progress
// for the session's lifetime, so navigating away and back re-shows what the
// run did, while a different conversation never bleeds another's steps.
// Ephemeral by design: nova-os doesn't persist progress events, so a page
// refresh starts empty.
const states = new Map<string, ProgressState>();
let currentConvId = "";
const listeners = new Set<() => void>();

function notify(): void {
  for (const l of listeners) l();
}

// Cached list of conversation ids with a live run — rebuilt only when a
// running flag actually flips so useSyncExternalStore consumers get a
// referentially stable snapshot.
let runningIds: string[] = [];

function recomputeRunning(): void {
  const next: string[] = [];
  for (const [id, s] of states) if (s.running) next.push(id);
  if (
    next.length !== runningIds.length ||
    next.some((id, i) => id !== runningIds[i])
  ) {
    runningIds = next;
  }
}

function set(convId: string, next: ProgressState): void {
  states.set(convId, next);
  recomputeRunning();
  // Listeners always re-poll the snapshot; useSyncExternalStore skips the
  // re-render when the current conversation's reference didn't change.
  notify();
}

function get(convId: string): ProgressState {
  return states.get(convId) ?? EMPTY;
}

export function subscribeProgress(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

export function getProgressSnapshot(): ProgressState {
  return get(currentConvId);
}

/** Progress for an explicit conversation (keep-alive decisions, #28). */
export function getProgressFor(convId: string): ProgressState {
  return get(convId);
}

/** Conversation ids with a live run — referentially stable between flips. */
export function getRunningConversationIds(): string[] {
  return runningIds;
}

/** Point the panel at a conversation. Called from the agent constructor —
 *  one agent instance per (subject, conversation), reconstructed on switch
 *  AND on auth-header rotation, so this must be idempotent for the same id
 *  (a token refresh mid-run must not wipe the live progress). */
export function progressSetConversation(convId: string): void {
  if (convId === currentConvId) return;
  currentConvId = convId;
  notify();
}

export function progressRunStarted(convId: string): void {
  set(convId, {
    running: true,
    phase: null,
    steps: [],
    skills: [],
    runStartedAt: Date.now(),
  });
}

export function progressRunFinished(convId: string): void {
  // Keep the assembled steps/skills around (so a finished run still shows
  // what it did when the user comes back to this conversation), but clear
  // the running flag + phase so the panel stops showing a live spinner.
  set(convId, { ...get(convId), running: false, phase: null });
}

// ---- defensive readers ------------------------------------------------

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;
}

function b64ToJson(s: string): unknown {
  try {
    // browser atob → JSON; tolerate url-safe base64.
    const norm = s.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = decodeURIComponent(escape(atob(norm)));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

function readPhase(value: unknown): string | null {
  if (typeof value === "string") return value || null;
  const r = asRecord(value);
  if (!r) return null;
  const k = r.content ?? r.phase ?? r.key ?? r.name;
  return typeof k === "string" ? k : null;
}

function coerceSteps(raw: unknown): PlanStep[] {
  const arr = Array.isArray(raw)
    ? raw
    : Array.isArray(asRecord(raw)?.steps)
      ? (asRecord(raw)!.steps as unknown[])
      : [];
  const out: PlanStep[] = [];
  for (let i = 0; i < arr.length; i++) {
    const s = asRecord(arr[i]);
    if (!s) continue;
    const id = String(s.id ?? s.task_id ?? s.taskId ?? i);
    out.push({
      id,
      description: String(s.description ?? s.desc ?? s.title ?? ""),
      capability: String(s.capability ?? s.required_capability ?? ""),
      status: "pending",
    });
  }
  return out;
}

function readPlan(value: unknown): PlanStep[] {
  // nova.plan ships {content:"<base64 of [{id,description,capability}]>"}.
  // Also tolerate raw string / {plan:"<b64>"} / {steps:[...]} / [...].
  if (typeof value === "string") return coerceSteps(b64ToJson(value) ?? []);
  const r = asRecord(value);
  if (r && typeof r.content === "string") return coerceSteps(b64ToJson(r.content) ?? []);
  if (r && typeof r.plan === "string") return coerceSteps(b64ToJson(r.plan) ?? []);
  return coerceSteps(value);
}

function readStep(value: unknown): { id: string; status: StepStatus } {
  // nova.step ships {content:"taskId:status"}. Also tolerate raw
  // "taskId:status" | {taskId, status} | {id, status}.
  const fromColon = (s: string): { id: string; status: StepStatus } => {
    const idx = s.lastIndexOf(":");
    return idx > 0
      ? { id: s.slice(0, idx), status: normStatus(s.slice(idx + 1)) }
      : { id: s, status: "started" };
  };
  if (typeof value === "string") return fromColon(value);
  const r = asRecord(value);
  if (!r) return { id: "", status: "started" };
  if (typeof r.content === "string") return fromColon(r.content);
  const id = String(r.id ?? r.task_id ?? r.taskId ?? "");
  return { id, status: normStatus(String(r.status ?? "started")) };
}

function normStatus(s: string): StepStatus {
  const v = s.trim().toLowerCase();
  if (v === "done" || v === "completed" || v === "complete") return "done";
  if (v === "error" || v === "failed" || v === "failure") return "error";
  return "started";
}

function readSkill(name: string, value: unknown): SkillRow | null {
  const r = asRecord(value) ?? {};
  const capability = String(r.capability ?? r.skill ?? "");
  const skill = String(r.skill ?? r.capability ?? "");
  if (!capability && !skill) return null;
  const status: SkillRow["status"] = name.endsWith("failed")
    ? "failed"
    : name.endsWith("completed")
      ? "done"
      : "running";
  const elapsed = r.elapsed_ms ?? r.elapsedMs;
  return {
    key: capability || skill,
    skill,
    capability,
    status,
    elapsedMs: typeof elapsed === "number" ? elapsed : undefined,
    error: typeof r.error === "string" ? r.error : undefined,
  };
}

/** Feed a single AG-UI CUSTOM event ({name, value}) into a conversation's
 *  progress. convId is captured by the agent instance that owns the run. */
export function handleNovaEvent(convId: string, name: string, value: unknown): void {
  const state = get(convId);
  switch (name) {
    case "nova.phase": {
      set(convId, { ...state, running: true, phase: readPhase(value) });
      break;
    }
    case "nova.plan": {
      const steps = readPlan(value);
      if (steps.length) set(convId, { ...state, running: true, steps });
      break;
    }
    case "nova.step": {
      const { id, status } = readStep(value);
      if (!id) break;
      set(convId, {
        ...state,
        running: true,
        steps: state.steps.map((s) =>
          s.id === id
            ? {
                ...s,
                status,
                startedAt:
                  status === "started" && !s.startedAt ? Date.now() : s.startedAt,
              }
            : s,
        ),
      });
      break;
    }
    case "nova.skill_started":
    case "nova.skill_completed":
    case "nova.skill_failed": {
      const row = readSkill(name, value);
      if (!row) break;
      const idx = state.skills.findIndex((s) => s.key === row.key);
      const skills = [...state.skills];
      if (idx >= 0) skills[idx] = { ...skills[idx], ...row };
      else skills.push(row);
      // Correlate elapsed time onto the matching plan step (by capability)
      // so the Chinese step list can show per-step timing.
      const steps =
        row.elapsedMs !== undefined
          ? state.steps.map((s) =>
              s.capability === row.capability
                ? { ...s, elapsedMs: row.elapsedMs }
                : s,
            )
          : state.steps;
      set(convId, { ...state, running: true, skills, steps });
      break;
    }
    default:
      break;
  }
}
